// WebsitesSalesSections.js - Marketing sections below the store list on the /websites ad landing page:
// pricing teaser and FAQ.
// Every section starts the free trial through the onStart callback (opens CustomWebsiteModal).

import React, { useEffect, useState } from 'react';
import { useUsd } from '../shared/signup/usd';

const DEFAULT_PRICES = { monthly: 399, yearly: 4499, firstMonthOffer: null };

// Live prices from the same endpoint the signup modal uses, so the page and modal always agree
function usePlanPrices() {
  const [prices, setPrices] = useState(DEFAULT_PRICES);

  useEffect(() => {
    let live = true;
    fetch('/api/onboarding/plans')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!live || !data || !Array.isArray(data.plans)) return;
        const updated = { ...DEFAULT_PRICES };
        data.plans.forEach((p) => {
          if ((p.key === 'monthly' || p.key === 'yearly') && typeof p.price === 'number') updated[p.key] = p.price;
          if (p.key === 'monthly' && p.firstMonthOffer) updated.firstMonthOffer = p.firstMonthOffer;
        });
        setPrices(updated);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  return prices;
}

export function PricingTeaser({ onStart }) {
  const prices = usePlanPrices();
  const offer = prices.firstMonthOffer;
  const { on: international, price: money, charged } = useUsd(); // US$ outside India, charged in ₹

  return (
    <section className="ws-section ws-pricing" id="ws-pricing" aria-labelledby="ws-pricing-title">
      <div className="container">
        <div className="ws-section-head">
          <span className="ws-kicker">SIMPLE PRICING</span>
          <h2 id="ws-pricing-title">Start free. Pay only if you love it.</h2>
          <p>No setup fee and no commission. Try everything free for 7 days, then choose a plan.</p>
        </div>

        <div className="ws-price-grid">
          <article className="ws-price-card ws-price-featured">
            <span className="ws-price-badge">Start here</span>
            <h3>7-Day Free Trial</h3>
            <div className="ws-price-amount">
              <strong>{money(0)}</strong> <span>for 7 days</span>
            </div>
            <ul>
              <li>Full store layout, ready for your products</li>
              <li>Free shopname.ownezzy.com link</li>
              <li>UPI checkout & WhatsApp alerts</li>
              <li>No credit card needed</li>
            </ul>
            <button type="button" className="ws-btn-primary" onClick={() => onStart('trial')}>
              Start Free Trial
            </button>
          </article>

          <article className="ws-price-card">
            {offer && <span className="ws-price-badge ws-badge-offer">Limited offer</span>}
            <h3>Monthly</h3>
            <div className="ws-price-amount">
              {offer ? (
                <>
                  <strong>{money(offer.price)}</strong> <span>first month</span>
                </>
              ) : (
                <>
                  <strong>{money(prices.monthly)}</strong> <span>/ month</span>
                </>
              )}
            </div>
            <p className="ws-price-hint">
              {offer ? `Then ${money(prices.monthly)}/month • new customers only` : `About ${money(Math.ceil(prices.monthly / 30))} a day`}
              {charged(offer ? offer.price : prices.monthly) && <><br />{charged(offer ? offer.price : prices.monthly)}</>}
            </p>
            <ul>
              <li>Everything in the trial</li>
              <li>0% sales commission</li>
              <li>Pay month to month</li>
              <li>Add your own custom domain</li>
            </ul>
            <button type="button" className="ws-btn-outline" onClick={() => onStart('monthly')}>
              Choose Monthly
            </button>
          </article>

          <article className="ws-price-card">
            <span className="ws-price-badge ws-badge-value">Best value</span>
            <h3>Yearly</h3>
            <div className="ws-price-amount">
              <strong>{money(prices.yearly)}</strong> <span>/ year</span>
            </div>
            <p className="ws-price-hint">
              About {money(Math.round(prices.yearly / 12))} a month
              {charged(prices.yearly) && <><br />{charged(prices.yearly)}</>}
            </p>
            <ul>
              <li>Everything in Monthly</li>
              <li>Save compared to paying monthly</li>
              <li>One payment for the whole year</li>
            </ul>
            <button type="button" className="ws-btn-outline" onClick={() => onStart('yearly')}>
              Choose Yearly
            </button>
          </article>
        </div>
        {international && <p className="ws-price-hint">Prices are shown in US$ for visitors outside India. Payment is charged in Indian Rupees (₹) by card, and your bank converts it, so the US$ prices are estimates.</p>}
      </div>
    </section>
  );
}

const FAQ = [
  ['Is the 7-day trial really free?', 'Yes. Your store is free for 7 days with full access, a free shopname.ownezzy.com address and no credit card. Plans start from ₹399/month only if you want to continue after the trial.'],
  ['What do I need to start?', 'Just your shop name, WhatsApp number and email. Verify your email with a 6-digit OTP and your store goes live in about 60 seconds, ready for you to add your own products.'],
  ['My business is not in the demo list. Can I still start?', 'Yes. Tap "Create Custom Website", pick from 100+ categories or type your own, and we set up a store for your business.'],
  ['Do you take commission on my sales?', 'No. Ownezzy charges 0% commission. Customers pay by UPI, cards or Cash on Delivery and the money goes straight to your bank account.'],
  ['Do I need a developer or technical skills?', 'No. You manage products, prices, banners and orders from a simple admin panel on your phone. Our team helps on WhatsApp if you get stuck.'],
  ['I didn\'t get the OTP email. What should I do?', 'Check your Spam or Promotions folder, then tap Resend after 30 seconds. You can also message us on WhatsApp and we will help you finish setup.'],
];

export function WebsitesFaq() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="ws-section ws-faq" aria-labelledby="ws-faq-title">
      <div className="container">
        <div className="ws-section-head">
          <span className="ws-kicker">QUESTIONS</span>
          <h2 id="ws-faq-title">Frequently asked questions</h2>
        </div>
        <div className="ws-faq-list">
          {FAQ.map(([question, answer], idx) => {
            const isOpen = openIndex === idx;
            return (
              <div className={`ws-faq-item ${isOpen ? 'open' : ''}`} key={question}>
                <button
                  type="button"
                  className="ws-faq-q"
                  aria-expanded={isOpen}
                  onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                >
                  <span>{question}</span>
                  <span className="ws-faq-icon" aria-hidden="true">{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && <p className="ws-faq-a">{answer}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
